import { useEffect, useMemo, useState } from 'react';
import { PAGE_VALUE_TYPE_KEYS, PAGE_VALUE_TYPES } from 'common/constants/notes';
import {
  EXPORT_WIDGET_NODE_ATTR,
  exportWidgetStatusAttr,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET,
} from 'common/constants/pdfExport';
import { ExportFooterData, ExportFooterNote } from 'common/types/pdfExport';
import { useDocuments } from 'services/hooks';
import { useGetNotes } from 'services/hooks/notes/useGetNotes';
import { UseGetNotesParams } from 'services/hooks/notes/useGetNotes/types';
import { Logger } from 'utilities';
import type { ExportWidgetRelayProps } from '../types';
import { htmlToPlainText } from '../utils';

export type { DocumentReferencesRelayProps, ExportWidgetRelayProps } from '../types';

/**
 * Supplies notes and document references to the PDF pipeline as text while the interactive
 * WidgetDrawer is hidden during capture. It uses DOM status signals to ensure the orchestrator
 * waits for data settlement before snapshotting the page.
 */
const ExportWidgetRelay = ({ notesProps, documentReferencesProps }: ExportWidgetRelayProps) => {
  const exportTarget = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET) ?? '';
  }, []);

  const statusAttr = useMemo(() => exportWidgetStatusAttr(exportTarget), [exportTarget]);

  // Notes are fetched reactively; documents imperatively (mirrors DocumentReferences.tsx).
  const notesQueryParams = useMemo<UseGetNotesParams>(
    () =>
      notesProps
        ? {
          pageType: notesProps.pageType,
          pageTypeKey: notesProps.pageTypeKey,
          pageTypeId: notesProps.pageTypeId,
          shouldQueryAutomatically: Boolean(exportTarget),
        }
        : {
          pageType: PAGE_VALUE_TYPES[0],
          pageTypeKey: PAGE_VALUE_TYPE_KEYS[0],
          shouldQueryAutomatically: false,
        },
    [notesProps, exportTarget]
  );
  const { data: notesData, isLoading: notesLoading } = useGetNotes(notesQueryParams);

  const { getReferencesByMeasurementDateId, foundReferencedDocuments } = useDocuments(
    documentReferencesProps?.selectedMeasurementDate as any
  );

  const [documents, setDocuments] = useState<string[] | null>(documentReferencesProps ? null : []);

  // Mark the relay as loading as early as possible so the capture pipeline
  // waits for it. Cleared on unmount.
  useEffect(() => {
    if (!exportTarget) {
      return undefined;
    }
    document.body.setAttribute(statusAttr, 'loading');
    return () => {
      document.body.removeAttribute(statusAttr);
      document.querySelector(`script[${EXPORT_WIDGET_NODE_ATTR}="${exportTarget}"]`)?.remove();
    };
  }, [exportTarget, statusAttr]);

  // Resolve the attached documents for this feature.
  useEffect(() => {
    if (!exportTarget || !documentReferencesProps) {
      return undefined;
    }
    const mdId = documentReferencesProps.selectedMeasurementDate?.id;
    const { currentPage, referencedFeatureId } = documentReferencesProps;
    if (mdId == null || currentPage == null || referencedFeatureId == null) {
      setDocuments([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const refs = await getReferencesByMeasurementDateId(mdId);
        const referenced = foundReferencedDocuments(Array.isArray(refs) ? refs : null, currentPage, referencedFeatureId);
        const filenames = referenced
          .map((doc: any) => (doc?.file ? `${doc.file.filename}.${doc.file.file_type}` : null))
          .filter((name: string | null): name is string => Boolean(name));
        if (!cancelled) {
          setDocuments(filenames);
        }
      } catch (error) {
        Logger.warn('ExportWidgetRelay: failed to resolve documents', error);
        if (!cancelled) {
          setDocuments([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // getReferencesByMeasurementDateId / foundReferencedDocuments are stable enough for a one-shot export fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportTarget, documentReferencesProps]);

  // Once both sources have settled, write the relay payload and flip to ready.
  const notesSettled = !notesProps || !notesLoading;
  const documentsSettled = documents !== null;

  useEffect(() => {
    if (!exportTarget || !notesSettled || !documentsSettled) {
      return;
    }

    const notes: ExportFooterNote[] = (notesData ?? [])
      .filter(note => note?.is_internal !== true && !note?.is_deleted)
      .map(note => ({ name: note?.name ?? undefined, text: htmlToPlainText(note?.content ?? '') }))
      .filter(note => Boolean(note.text) || Boolean(note.name));

    const payload: ExportFooterData = { notes, documents: documents ?? [] };

    let node = document.querySelector<HTMLScriptElement>(`script[${EXPORT_WIDGET_NODE_ATTR}="${exportTarget}"]`);
    if (!node) {
      node = document.createElement('script');
      node.type = 'application/json';
      node.setAttribute(EXPORT_WIDGET_NODE_ATTR, exportTarget);
      document.body.appendChild(node);
    }
    node.textContent = JSON.stringify(payload);
    document.body.setAttribute(statusAttr, 'ready');
  }, [exportTarget, statusAttr, notesSettled, documentsSettled, notesData, documents]);

  return null;
};

export default ExportWidgetRelay;
